<template>
  <div class="container">
    <!-- Success and Error Messages -->
    <div v-if="successMessage" class="alert alert-success alert-dismissible fade show fixed-alert" role="alert">
      {{ successMessage }}
      <button type="button" class="btn-close" @click="successMessage = ''"></button>
    </div>

    <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show fixed-alert" role="alert">
      {{ errorMessage }}
      <button type="button" class="btn-close" @click="errorMessage = ''"></button>
    </div>

    <!-- Page Title -->
    <div class="mt-4">
      <h2>{{ $t('positions.title') }}</h2>
    </div>

    <!-- Search Input -->
    <div class="mb-4">
      <input
          v-model="query"
          @input="searchPositions"
          class="form-control"
          type="text"
          :placeholder="$t('search_placeholder')"
      />
    </div>

    <!-- Add Position Button -->
    <div class="mb-4">
      <button v-if="hasPermission('users.add_position')" class="btn btn-primary" @click="openModal('add')">
        <i class="bi bi-plus-circle"></i> {{ $t('add_button') }}
      </button>
    </div>

    <!-- Position List -->
    <div class="columns is-multiline">
      <div class="column is-12">

        <!-- Pagination Buttons -->
        <div class="pagination-controls">
          <button
              class="btn pagination-btn"
              @click="goToPreviousPage()"
              v-if="showPreviousButton"
          >
            <i class="bi bi-arrow-left"></i>
            {{ $t('previous') }}
          </button>
          <button
              class="btn pagination-btn"
              @click="goToNextPage()"
              v-if="showNextButton"
          >
            {{ $t('next') }}
            <i class="bi bi-arrow-right"></i>
          </button>
        </div>

        <template v-if="positions.length">
          <table class="table table-hover">
            <thead>
            <tr>
              <th>{{ $t('id') }}</th>
              <th>{{ $t('position') }}</th>
              <th>{{ $t('job_level') }}</th>
              <th>{{ $t('actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="position in positions" :key="position.id">
              <td>{{ position.id }}</td>
              <td>{{ position.name }}</td>
              <td>{{ getName(position.job_level) }} ({{ getLevel(position.job_level) }})</td>
              <td>
                <!-- Edit button -->
                <button v-if="hasPermission('users.change_position')" class="btn btn-primary btn-sm"
                        @click="openModal('edit', position)">
                  <i class="bi bi-pencil-square"></i> {{ $t('edit') }}
                </button>
                <!-- Delete button -->
                <button v-if="hasPermission('users.delete_position')" @click="openConfirmDelete(position.id)"
                        class="btn btn-danger btn-sm">
                  <i class="bi bi-trash"></i> {{ $t('delete') }}
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>

        <!-- No positions available -->
        <div v-else>
          <p>{{ $t('no_positions') }}</p>
        </div>
      </div>
    </div>

    <!-- Bootstrap Modal for Add/Edit Position -->
    <div class="modal fade" id="positionModal" tabindex="-1" aria-labelledby="positionModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="positionModalLabel">
              {{ isEditMode ? $t('positions.edit_title') : $t('positions.add_title') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <div class="modal-body">
            <!-- Add/Edit Form -->
            <form @submit.prevent="submitPosition">
              <div class="mb-3">
                <label for="positionName" class="form-label">{{ $t('position_name') }}</label>
                <input v-model="positionForm.name" type="text" class="form-control" id="positionName" required/>
              </div>
              <div class="mb-3">
                <label for="jobLevelSelect" class="form-label">{{ $t('job_level') }}</label>
                <select v-model.number="positionForm.job_level" class="form-select" id="jobLevelSelect" required>
                  <option v-for="jobLevel in jobLevels" :key="jobLevel.id" :value="jobLevel.id">
                    {{ jobLevel.name }} ({{ jobLevel.level }})
                  </option>
                </select>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{
                $t('cancel')
              }}
            </button>
            <button type="button" class="btn btn-primary" @click="submitPosition">
              {{ isEditMode ? $t('positions.update') : $t('positions.add') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Confirm Delete Modal -->
    <div v-if="showConfirm" class="modal-overlay">
      <div class="modal-container">
        <h3 class="modal-title">{{ $t('confirm_delete') }}</h3>
        <p>{{ $t('positions.confirm_delete') }}</p>
        <div class="modal-actions">
          <button class="btn btn-danger" @click="deletePosition">{{ $t('confirm') }}</button>
          <button class="btn btn-primary" @click="cancelDelete">{{ $t('cancel') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useMainStore } from '@/store';
import axios from 'axios';

export default {
  name: 'PositionList',
  data() {
    return {
      positions: [],
      jobLevels: [],
      showNextButton: false,
      showPreviousButton: false,
      currentPage: 1,
      query: '',
      successMessage: '',
      errorMessage: '',
      isEditMode: false,
      positionForm: {
        id: null,
        name: '',
        job_level: null,
      },
      modalInstance: null,
      showConfirm: false,
      positionIdToDelete: null,
    };
  },
  mounted() {
    this.getJobLevels();
    this.getPositions();
    this.$nextTick(() => {
      const modalElement = document.getElementById('positionModal');
      if (modalElement) {
        this.modalInstance = new bootstrap.Modal(modalElement);
      }
    });
  },
  computed: {
    store: () => useMainStore(),
    hasPermission() {
      return (permission) => {
        const userPermissions = this.store.user.permissions || [];
        return userPermissions.includes(permission);
      };
    }
  },

  methods: {
    openModal(mode, position = null) {
      if (mode === 'add') {
        this.isEditMode = false;
        this.positionForm = {id: null, name: '', job_level: null};
      } else if (mode === 'edit') {
        this.isEditMode = true;
        this.positionForm = {
          id: position.id,
          name: position.name,
          // position.job_level is an object from read serializer; use its id
          job_level: position.job_level ? Number(position.job_level.id) : null,
        };
      }
      this.modalInstance.show();
    },
    closeModal() {
      this.modalInstance.hide();
    },
    async submitPosition() {
      try {
        if (this.isEditMode) {
          await axios.put(`/api/v1/admin/positions/${this.positionForm.id}/`, {
            name: this.positionForm.name,
            job_level: this.positionForm.job_level,
          });
          this.successMessage = 'Position updated successfully';
        } else {
          await axios.post(`/api/v1/admin/positions/`, {
            name: this.positionForm.name,
            job_level: this.positionForm.job_level,
          });
          this.successMessage = 'Position added successfully';
        }
        this.getPositions();
      } catch (error) {
        this.errorMessage = 'Failed to save position';
      } finally {
        this.closeModal();
        setTimeout(() => {
          this.successMessage = '';
          this.errorMessage = '';
        }, 3000);
      }
    },
    openConfirmDelete(positionId) {
      this.positionIdToDelete = positionId;
      this.showConfirm = true;
    },
    async deletePosition() {
      try {
        await axios.delete(`/api/v1/admin/positions/${this.positionIdToDelete}/`);
        this.successMessage = 'Position deleted successfully';
        this.getPositions();
      } catch (error) {
        this.errorMessage = 'Failed to delete position';
      } finally {
        this.showConfirm = false;
        setTimeout(() => {
          this.successMessage = '';
          this.errorMessage = '';
        }, 3000);
      }
    },
    cancelDelete() {
      this.showConfirm = false;
    },
    async getPositions() {
      try {
        const response = await axios.get(`/api/v1/admin/positions/?page=${this.currentPage}&search=${this.query}`);
        this.positions = response.data.results;
        this.showNextButton = Boolean(response.data.next);
        this.showPreviousButton = Boolean(response.data.previous);
      } catch (error) {
        this.errorMessage = 'Failed to fetch positions';
        setTimeout(() => {
          this.errorMessage = '';
        }, 3000);
      }
    },
    async getJobLevels() {
      try {
        const response = await axios.get('/api/v1/admin/job-levels/');
        this.jobLevels = response.data.results;
      } catch (error) {
        console.error('Failed to fetch job levels', error);
      }
    },
    getName(jl) {
      return jl && jl.name ? jl.name : this.$t('joblevel_no_name')
    },
    getLevel(jl) {
      return jl && jl.level != null ? jl.level : ('joblevel_no_level')
    },
    searchPositions() {
      this.currentPage = 1;
      this.getPositions();
    },
    goToNextPage() {
      this.currentPage += 1;
      this.getPositions();
    },
    goToPreviousPage() {
      this.currentPage -= 1;
      this.getPositions();
    },
  },
};
</script>
